import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import {
    getResidentialUnitDocumentVersions,
    getDocumentFileType,
    getOpenDocumentUrl,
    getProjectDocumentVersions,
    getDocumentThumbnailUrl,
    getDownloadDocumentUrl,
} from '@/services/documents';
import theme from './DocumentSelectList.module.scss';
import { LoadingIndicator, Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';
import { FileType } from '@ygtechnologies/rubus-frontend-utilities';
import { classnames } from '@/utils/html';
import { DateDisplay } from '@/components/localization';
import {
    ResidentialUnitDocument,
    ResidentialUnitDocumentVersion,
    DocumentStatus,
    BaseDocument,
    DocumentVersion,
    BuyerResidentialUnitDocument,
} from '@/types';
import { SimpleCard } from '@/components/Cards';
import { useAsyncState } from '@/utils/hooks';
import { DocumentSelectionMode, DocumentSelectionValue } from '.';
import { Radio } from '@/components/forms/Radio';
import { LightboxSingleImage } from '@ygtechnologies/rubus-ui';

export interface DocumentListItemDetailsProps {
    document: BaseDocument | BuyerResidentialUnitDocument;
    onSelectionChange: (newValue: DocumentSelectionValue) => void;
    selectedValue: DocumentSelectionValue;
    selectMode: DocumentSelectionMode;
}

const DocumentSelectListItemDetails: React.FC<DocumentListItemDetailsProps> = ({
    document,
    selectMode,
    onSelectionChange,
    selectedValue,
}) => {
    const residentialUnitId = (document as ResidentialUnitDocument | BuyerResidentialUnitDocument).residentialUnitId ?? null;

    const [versions] = useAsyncState<DocumentVersion[] | null>(
        () =>
            residentialUnitId
                ? getResidentialUnitDocumentVersions(document.documentId, residentialUnitId)
                : getProjectDocumentVersions(document.documentId),
        [document.documentId, residentialUnitId],
        null
    );

    const [lightboxDocument, setLightboxDocument] = useState<DocumentVersion | null>(null);

    const handleThumbnailClick = (e, version) => {
        if (getDocumentFileType(version) === FileType.Image) {
            setLightboxDocument(version);

            e.preventDefault();
        }
    };

    const handleLightboxClose = () => {
        setLightboxDocument(null);
    };

    if (!versions) {
        return <LoadingIndicator />;
    }

    const latestApprovedVersion = versions!.find(
        (x) => (x as ResidentialUnitDocumentVersion).status === DocumentStatus.Approved
    );

    return (
        <div className={theme.detailsContainer}>
            <SimpleCard className={theme.card}>
                <div className={theme.versionList}>
                    {versions.map((version) => (
                        <div className={theme.versionContainer} key={version.versionId}>
                            <div
                                className={classnames({
                                    [theme.version]: true,
                                    [theme.latest]: latestApprovedVersion === version,
                                })}>
                                <div className={classnames(theme.text, theme.top)}>
                                    {latestApprovedVersion === version ? (
                                        <Trans>Latest version</Trans>
                                    ) : (
                                        <Trans>Version {version.versionNumber}</Trans>
                                    )}
                                </div>

                                {selectMode === 'version' && (
                                    <Radio
                                        onChange={() => onSelectionChange(version.versionId)}
                                        checked={version.versionId === selectedValue}
                                        labelClassName={theme.select}
                                    />
                                )}

                                <a
                                    href={getOpenDocumentUrl(version.versionId)}
                                    className={theme.thumbnail}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => handleThumbnailClick(e, version)}>
                                    <Thumbnail
                                        src={getDocumentThumbnailUrl(version.versionId)}
                                        fileMetadata={version.fileMetadata}
                                    />
                                </a>

                                <div className={classnames(theme.text, theme.bottom)}>
                                    {version.name}

                                    <DateDisplay value={version.uploadDate} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </SimpleCard>

            <LightboxSingleImage
                image={{
                    src: lightboxDocument ? getDownloadDocumentUrl(lightboxDocument.versionId) : '',
                    thumbnailSrc: lightboxDocument ? getDownloadDocumentUrl(lightboxDocument.versionId) : '',
                    title: lightboxDocument?.name || '',
                    description: lightboxDocument?.description || '',
                    type: 'image',
                }}
                open={!!lightboxDocument}
                setOpen={handleLightboxClose}
            />
        </div>
    );
};

export default DocumentSelectListItemDetails;
